import { useCallback, useEffect, useRef, useState } from "react";
import JsSIP from "jssip";
import { useCallTones } from "./useCallTones";
import type { CallContext, CallState, RegState, SipCredentials } from "./types";

// ─── SIP phone ─────────────────────────────────────────────────────────────
// Registration, the single active RTC session, and the call verbs. Lifted
// from the working webphone with the logic intact — in particular
// no_answer_timeout, the _tracksAttached guard, the outgoing-only ringback
// condition and the shared ended/failed teardown are all load-bearing and
// documented below.
//
// This hook does not know about conference rooms. The caller passes
// onConfirmed / onEnded (wired to useConference's promote / reset), which
// keeps the two layers independent.

interface UseSipPhoneOptions {
    credentials: SipCredentials;
    /** Called once the call is up. Used to promote it into its conference room. */
    onConfirmed?: () => void | Promise<void>;
    /** Called on ended OR failed. Used to clear conference state. */
    onEnded?: () => void;
    /** Called when an inbound INVITE arrives, with any X-Vca-* context on it. */
    onIncoming?: (ctx: CallContext) => void;
}

export function useSipPhone({ credentials, onConfirmed, onEnded, onIncoming }: UseSipPhoneOptions) {
    const { sipUser, sipPass, sipDomain, wssUri } = credentials;

    const [reg, setReg] = useState<RegState>("init");
    const [call, setCall] = useState<CallState>("idle");
    const [remoteId, setRemoteId] = useState("");
    const [secs, setSecs] = useState(0);
    const [lastDtmf, setLastDtmf] = useState("");
    // Context off the inbound INVITE's custom headers. Null for outbound and
    // for direct inbound calls that didn't come through the queue.
    const [callContext, setCallContext] = useState<CallContext | null>(null);
    // Wall-clock start, exposed alongside secs so the agent layer can derive
    // its own durations without running a second interval.
    const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
    // Direction is captured when the session starts, because it's needed
    // AFTER the call goes active (to tell an answered inbound apart from a
    // connected outbound) and the call state alone can't distinguish them.
    const [direction, setDirection] = useState<"in" | "out" | null>(null);

    const uaRef = useRef<any>(null);
    const sessRef = useRef<any>(null);
    // Every session currently alive, not just the one the UI is showing.
    // sessRef alone was a single slot, so a second session overwrote the
    // first and left it un-answerable, un-hangup-able and still ringing.
    const sessionsRef = useRef<Set<any>>(new Set());
    const audioRef = useRef<HTMLAudioElement>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const tones = useCallTones();
    const { start: startTone, stop: stopTone } = tones;

    // Callbacks change identity on most renders; keeping them in refs stops
    // the UA effect below from tearing down and re-registering the phone
    // every time the agent UI re-renders.
    const onConfirmedRef = useRef(onConfirmed);
    const onEndedRef = useRef(onEnded);
    const onIncomingRef = useRef(onIncoming);
    useEffect(() => {
        onConfirmedRef.current = onConfirmed;
        onEndedRef.current = onEnded;
        onIncomingRef.current = onIncoming;
    }, [onConfirmed, onEnded, onIncoming]);

    // Build the remote stream from the peer connection's RECEIVERS rather
    // than from the ontrack event's `streams[0]`.
    //
    // Two reasons, both of which bite on this setup specifically:
    //
    // 1. FreeSWITCH frequently omits a=msid from its SDP. With no msid the
    //    track arrives with an empty `streams` array, so `streams[0]` is
    //    undefined, the old `if (audioRef.current && s)` test failed, and
    //    nothing was ever assigned. Receivers are always populated.
    //
    // 2. A queue-answered call is re-INVITEd. The agent leg is originated
    //    with &park(), answered, and only then transferred into the
    //    conference — `uuid_transfer <uuid> conference:<room>@adhoc`. That
    //    transfer swaps the media path from the park app to the conference
    //    mixer mid-call. The first stream the browser latched onto is the
    //    park one, and if ontrack does not fire again for the replacement
    //    (it often does not, when the transceiver is reused) the element
    //    stays pointed at dead media. Re-reading the receivers every time
    //    signaling returns to `stable` picks the new media up on every
    //    re-INVITE, with no reliance on ontrack firing at all.
    const attachRemote = useCallback((pc: any) => {
        if (!pc || !audioRef.current) return;
        const stream = new MediaStream();
        for (const r of pc.getReceivers?.() || []) {
            if (r.track && r.track.kind === "audio") stream.addTrack(r.track);
        }
        if (stream.getAudioTracks().length === 0) {
            console.log("[SIP] attachRemote — no audio receivers yet, skipping");
            return;
        }
        audioRef.current.srcObject = stream;
        audioRef.current.muted = false;
        audioRef.current
            .play()
            .then(() => console.log("[SIP] remote audio playing, tracks=", stream.getAudioTracks().length))
            .catch((e: any) => console.error("[SIP] play() failed:", e));
    }, []);

    const attachTrack = useCallback(
        (pc: any) => {
            if (!pc || pc._handlersAttached) return; // guard against double-registering handlers
            pc._handlersAttached = true;
            console.log("[SIP] attaching handlers — signalingState=", pc.signalingState);
            pc.ontrack = (e: any) => {
                console.log("[SIP] ontrack  kind=", e?.track?.kind, " streams=", e?.streams?.length);
                attachRemote(pc);
            };
            // The re-INVITE hook. Fires every time an offer/answer exchange
            // completes, which is exactly when the conference transfer's new
            // media becomes current.
            pc.onsignalingstatechange = () => {
                console.log("[SIP] signaling state →", pc.signalingState);
                if (pc.signalingState === "stable") attachRemote(pc);
            };
            pc.oniceconnectionstatechange = () => console.log("[SIP] ICE state →", pc.iceConnectionState);
            pc.onconnectionstatechange = () => console.log("[SIP] connection state →", pc.connectionState);
        },
        [attachRemote],
    );

    const wireSession = useCallback(
        (sess: any) => {
            console.log("[SIP] wireSession  connection already exists=", !!sess.connection);
            if (sess.connection) attachTrack(sess.connection);
            sess.on("peerconnection", ({ peerconnection: pc }: any) => {
                console.log("[SIP] peerconnection event fired");
                attachTrack(pc);
            });
            sess.on("connecting", () => console.log("[SIP] connecting"));
            sess.on("progress", (e: any) => {
                console.log("[SIP] progress", e?.response?.status_code);
                // Far end is ringing — play ringback so the agent knows the
                // call is actually progressing rather than sitting silent.
                // Only for outbound: on an inbound call this fires for our
                // own 180 Ringing, where the inbound tone is already playing.
                if (sess.direction === "outgoing") startTone("outbound");
            });
            sess.on("confirmed", async () => {
                console.log("[SIP] confirmed — call active");
                // Only the session the UI is actually showing may drive the
                // UI. See the note on `end` below.
                if (sessRef.current !== sess) {
                    console.warn("[SIP] confirmed on a non-current session — ignoring for UI");
                    return;
                }
                // Re-read the receivers here too. On an inbound call the
                // answer completes after ontrack has already fired, so this
                // is the first point at which the receivers are guaranteed
                // to be populated.
                if (sess.connection) attachRemote(sess.connection);
                stopTone(); // answered — silence any ringing/ringback
                setCall("active");
                setSecs(0);
                setCallStartedAt(Date.now());
                timerRef.current = setInterval(() => setSecs((n) => n + 1), 1000);
                await onConfirmedRef.current?.();
            });
            // Termination handler, scoped to ITS OWN session.
            //
            // This used to reset the whole UI and null sessRef unconditionally,
            // whichever session fired it. With two sessions alive at once that
            // is destructive: session A ending wiped the UI belonging to
            // session B, leaving B ringing at the SIP level with no visible
            // call and the agent's status still "Available" — exactly the
            // reported "still hear ringing but cannot see a call". B then kept
            // ringing until the server's 5-minute originate timeout, bled into
            // the next test, and made an outbound call ring on top of it.
            // That contamination is why one tester logged outbound audio as
            // broken while another, testing clean, logged it as fine.
            const end = (e?: any) => {
                const cause = e?.cause || e?.message || "unknown";
                const code = e?.response?.status_code || "";
                const isCurrent = sessRef.current === sess;
                console.log("[SIP] ended/failed  cause=", cause, " code=", code, " current=", isCurrent);
                sessionsRef.current.delete(sess);
                // A tone belongs to the call that started it. Silence it only
                // once nothing is left that should be making noise.
                if (sessionsRef.current.size === 0) stopTone();
                if (!isCurrent) return; // a background session ending must not touch the UI
                if (timerRef.current) clearInterval(timerRef.current);
                timerRef.current = null;
                setCall("idle");
                setRemoteId("");
                setSecs(0);
                setCallStartedAt(null);
                setCallContext(null);
                setDirection(null);
                sessRef.current = null;
                if (audioRef.current) audioRef.current.srcObject = null;
                onEndedRef.current?.();
            };
            sess.on("ended", end);
            sess.on("failed", end);
        },
        [attachTrack, attachRemote, startTone, stopTone],
    );

    useEffect(() => {
        JsSIP.debug.disable("JsSIP:*");
        const ua = new JsSIP.UA({
            sockets: [new JsSIP.WebSocketInterface(wssUri)],
            uri: `sip:${sipUser}@${sipDomain}`,
            password: sipPass,
            register: true,
            session_timers: false,
            // CONFIRMED via SIP trace as the real cause of queued calls
            // dying at ~61s: JsSIP rejects an unanswered incoming INVITE
            // after this many seconds by sending 408 Request Timeout —
            // default is 60. FreeSWITCH then logs it as
            // RECOVERY_ON_TIMER_EXPIRE, which misleadingly looks like a
            // SIP session timer (RFC 4028) issue; it isn't — FreeSWITCH's
            // own INVITE carries no Session-Expires header at all.
            // Raised to 600 so it comfortably outlasts the queue's own
            // 5-minute total_ring_time cap, which should always be what
            // actually ends a waiting call.
            no_answer_timeout: 600,
        });
        ua.on("registered", () => setReg("ok"));
        ua.on("unregistered", () => setReg("gone"));
        ua.on("registrationFailed", (e: any) => {
            console.error("SIP reg failed", e);
            setReg("fail");
        });
        ua.on("newRTCSession", ({ session }: any) => {
            // Refuse a second concurrent incoming call rather than silently
            // replacing the first. One agent is one line: the server's own
            // busy checks are supposed to prevent a second offer, but when
            // one slips through (a kill that raced, a re-offer of the same
            // caller) overwriting sessRef orphaned the first session — it
            // stayed ringing at the SIP level with nothing able to answer or
            // end it, for the full 5-minute originate timeout.
            //
            // 486 Busy Here is the correct, explicit answer, and it tells the
            // ring engine to move on instead of leaving a leg hanging.
            if (session.direction === "incoming" && sessRef.current && sessionsRef.current.has(sessRef.current)) {
                console.warn("[SIP] second incoming session while one is already live — rejecting with 486");
                try {
                    session.terminate({ status_code: 486, reason_phrase: "Busy Here" });
                } catch (err) {
                    console.error("[SIP] failed to reject duplicate session:", err);
                }
                return;
            }
            sessionsRef.current.add(session);
            sessRef.current = session;
            wireSession(session);
            if (session.direction === "incoming") {
                // Custom headers set by the queue's originate (server.js).
                // Absent on direct calls that didn't come through the queue,
                // which is why every field is optional.
                const header = (name: string) => {
                    try {
                        return session.request?.getHeader(name) || undefined;
                    } catch {
                        return undefined;
                    }
                };
                const ctx: CallContext = {
                    callSid: header("X-Vca-Call-Sid"),
                    did: header("X-Vca-Did"),
                    caller: header("X-Vca-Caller"),
                };
                setCallContext(ctx);
                // Prefer the real caller from the header. remote_identity is
                // origination_caller_id_number (MAIN_DID) on every queued
                // call, so it's the same constant string for all of them and
                // useless on its own.
                setRemoteId(ctx.caller || session.remote_identity?.uri?.user || "Unknown");
                setDirection("in");
                setCall("in");
                startTone("inbound"); // audible ring, not just a visual one
                onIncomingRef.current?.(ctx);
            }
        });
        ua.start();
        uaRef.current = ua;
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            ua.stop();
        };
    }, [sipUser, sipPass, sipDomain, wssUri, wireSession, startTone]);

    // ── Call verbs ─────────────────────────────────────────────────────────

    // callerId is sent as a custom header for ypc_outbound.xml to read. Until
    // that dialplan change lands the call still connects, it just presents
    // MAIN_DID — so this is safe to ship ahead of the backend.
    const dial = useCallback(
        (target: string, callerId?: string) => {
            const t = (target || "").trim();
            if (!t || !uaRef.current || reg !== "ok") return;
            uaRef.current.call(`sip:${t}@${sipDomain}`, {
                mediaConstraints: { audio: true, video: false },
                // mediaConstraints only controls what we CAPTURE. Without the
                // offer constraints below, JsSIP still advertises a
                // receive-only video stream, FreeSWITCH negotiates video, and
                // the conference member joins flagged video|vid-floor. This
                // build can't mix video (libpng missing — it throws at the
                // moment that member joins) and the call ends up silent.
                rtcOfferConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 0 },
                pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
                extraHeaders: callerId ? [`X-Vca-Caller-Id: ${callerId}`] : undefined,
            });
            setRemoteId(t);
            setDirection("out");
            setCall("out");
        },
        [reg, sipDomain],
    );

    const answer = useCallback(() => {
        stopTone(); // cut the ring immediately on click, don't wait for 'confirmed'
        sessRef.current?.answer({
            mediaConstraints: { audio: true, video: false },
            // Same reason as in dial() above — stops the answer advertising a
            // receive-only video stream.
            rtcAnswerConstraints: { offerToReceiveAudio: 1, offerToReceiveVideo: 0 },
            pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
        });
    }, [stopTone]);

    const reject = useCallback(() => {
        stopTone();
        sessRef.current?.terminate({ status_code: 486, reason_phrase: "Busy Here" });
    }, [stopTone]);

    // Doubles as "leave the call" once promoted into a multi-party
    // conference: it only ends YOUR OWN SIP session. FreeSWITCH conferences
    // keep running as long as anyone's still in them, so everyone else stays
    // connected — no separate "Leave" action needed.
    const hangup = useCallback(() => {
        console.log("[HANG] sessions=", sessionsRef.current.size, "callState=", call);
        if (sessionsRef.current.size === 0) {
            console.warn("[HANG] no session");
            // Belt and braces: if the UI thinks a tone is playing with no
            // session behind it, silence it. An agent should never be left
            // listening to a ring they cannot stop.
            stopTone();
            return;
        }
        // Terminate EVERY live session, not just the current one. Hanging up
        // must leave nothing behind — a session the UI had lost track of was
        // previously unreachable here and kept ringing on its own.
        // Array.from, not [...set]: the VCA compiles to ES5, where spreading
        // a Set is a compile error (TS2802).
        for (const s of Array.from(sessionsRef.current)) {
            try {
                s.terminate();
            } catch (e) {
                console.error("[HANG] terminate failed:", e);
                sessionsRef.current.delete(s);
            }
        }
    }, [call, stopTone]);

    const sendDTMF = useCallback((digit: string) => {
        if (!sessRef.current) return;
        try {
            sessRef.current.sendDTMF(digit, { transportType: "INFO", duration: 160, interToneGap: 50 });
            setLastDtmf(digit);
            setTimeout(() => setLastDtmf(""), 600);
            console.log("[DTMF] sent:", digit);
        } catch (e: any) {
            console.error("[DTMF] error:", e.message);
        }
    }, []);

    return {
        reg,
        call,
        direction,
        remoteId,
        secs,
        callStartedAt,
        callContext,
        lastDtmf,
        audioRef,
        dial,
        answer,
        reject,
        hangup,
        sendDTMF,
    };
}
